import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProfileDropdown from '@/shared/components/templates/ProfileDropdown.jsx';
import ThemeToggle from '@/shared/components/templates/ThemeToggle.jsx';
import Select from '@/shared/components/elements/Select.jsx';
import useTenantList from '@/shared/hooks/useTenantList.js';
import useSelectedTenant from '@/shared/hooks/useSelectedTenant.js';
import useDebounce from '@/shared/hooks/useDebounce.js';
import CreditQuickAccess from '@/feature/Billing/components/CreditQuickAccess.jsx';

export default function Header({ hasSecondarySidebar, isPrimaryHovered }) {
  const navigate = useNavigate();
  const isCollapsed = useSelector((state) => state.sidebar.isCollapsed);

  // --- Search Logic ---
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [hasEnoughItemsForSearch, setHasEnoughItemsForSearch] = useState(false);

  // --- Custom Hooks ---
  const { tenantList, isLoading } = useTenantList(debouncedSearchTerm);
  const { tenantId, setTenantId } = useSelectedTenant();

  // --- Effects ---
  useEffect(() => {
    if (!debouncedSearchTerm) {
      setHasEnoughItemsForSearch(tenantList.length > 5);
    }
  }, [tenantList.length, debouncedSearchTerm]);

  // --- Handlers ---
  const handleTenantChange = useCallback(
    (value) => {
      setTenantId(String(value));
      navigate('/');
    },
    [setTenantId, navigate],
  );

  const handleManageTenants = useCallback(() => {
    navigate('/tenant');
  }, [navigate]);

  // --- Derived State ---
  const displayValue = tenantId
    ? Number(tenantId)
    : tenantList.length > 0
      ? tenantList[0].value
      : null;

  const getHeaderRight = () => {
    if (!hasSecondarySidebar) {
      return isCollapsed ? 'right-16' : 'right-72';
    }
    if (isPrimaryHovered) {
      return 'right-[304px]';
    }
    return 'right-[280px]';
  };

  return (
    <header
      className={`border-tertiary-10 shadow-tertiary-6 fixed top-0 left-0 z-40 h-16 border-b bg-white/95 shadow-lg backdrop-blur-xl transition-all duration-300 ease-in-out dark:border-gray-800/50 dark:bg-gray-900/95 dark:shadow-none ${getHeaderRight()}`}
    >
      <div className="flex h-full items-center justify-between px-6">
        <div className="flex w-auto items-center gap-4">
          <div className="w-64">
            <Select
              options={tenantList}
              value={displayValue}
              onChange={handleTenantChange}
              placeholder="انتخاب مرکز ..."
              size="sm"
              loading={isLoading}
              emptyMessage={isLoading ? 'در حال جستجو...' : 'مرکزی یافت نشد'}
              showManageButton={true}
              onManageClick={handleManageTenants}
              manageButtonText="مدیریت مراکز"
              showSearch={hasEnoughItemsForSearch}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
          <CreditQuickAccess />
          <ProfileDropdown />
        </div>
      </div>
    </header>
  );
}
